import { useEffect, useMemo, useRef, useState } from "react";
import { normalizeVisualExecutionMode, resolveVisualRuntimeExecutionMode } from "../../visualExecutionModes";
import { useDashboardDomainContext } from "../../DashboardDomainProvider";
import { useVisualPreviewData } from "./useVisualPreviewData";

const buildRuntimeSignature = (visual) => {
  if (!visual) {
    return "";
  }

  try {
    return JSON.stringify({
      VisualId: visual?.VisualId,
      UniqueId: visual?.UniqueId,
      ModelKind: visual?.ModelKind,
      ModelId: visual?.ModelId,
      VisualTypeId: visual?.VisualTypeId,
      ExecutionMode: visual?.ExecutionMode,
      VisualColumns: visual?.VisualColumns ?? [],
      RenderSettings: visual?.RenderSettings ?? {},
      DefaultQueryState: visual?.DefaultQueryState ?? {},
    });
  } catch {
    return `${visual?.VisualId ?? visual?._id ?? ""}:${visual?.ModelId ?? ""}:${visual?.VisualTypeId ?? ""}`;
  }
};

export const useRealtimeVisualData = ({ visual }) => {
  const {
    selectedPageData,
    visualRefreshCycleId,
    markVisualRefreshSettled,
    isViewMode,
  } = useDashboardDomainContext();
  const visualId = useMemo(
    () => String(visual?.VisualId ?? visual?._id ?? visual?.UniqueId ?? ""),
    [visual?._id, visual?.UniqueId, visual?.VisualId]
  );
  const previewRuntime = useVisualPreviewData({
    visual,
    pageKey: selectedPageData?.Key ?? null,
    refreshCycleId: visualRefreshCycleId,
    onRefreshSettled: markVisualRefreshSettled,
  });
  const runtimeSignature = useMemo(() => buildRuntimeSignature(visual), [visual]);
  const executionMode = useMemo(() => {
    const selectedExecutionMode = normalizeVisualExecutionMode(
      Number(visual?.ModelKind ?? 0),
      visual?.ExecutionMode,
      visual?.DefaultQueryState?.SupportsRealTime ?? true
    );

    return resolveVisualRuntimeExecutionMode(selectedExecutionMode, isViewMode);
  }, [isViewMode, visual?.DefaultQueryState?.SupportsRealTime, visual?.ExecutionMode, visual?.ModelKind]);
  const [lastLoadedAt, setLastLoadedAt] = useState(null);
  const previousRenderSignatureRef = useRef("");
  const lastCompletedRefreshCycleRef = useRef(0);

  useEffect(() => {
    previousRenderSignatureRef.current = "";
    setLastLoadedAt(null);
    lastCompletedRefreshCycleRef.current = 0;
  }, [runtimeSignature]);

  useEffect(() => {
    if (previewRuntime?.isLoading) {
      return;
    }

    if (!previewRuntime?.renderModel || previewRuntime?.error) {
      if (!previewRuntime?.renderModel) {
        previousRenderSignatureRef.current = "";
        setLastLoadedAt(null);
      }
      return;
    }

    let nextRenderSignature = "";
    try {
      nextRenderSignature = JSON.stringify({
        UpdatedDate: previewRuntime?.renderModel?.UpdatedDate ?? "",
        OptionJson: previewRuntime?.renderModel?.OptionJson ?? "",
        VisualId:
          previewRuntime?.renderModel?.VisualId
          ?? previewRuntime?.renderModel?._id
          ?? previewRuntime?.renderModel?.UniqueId
          ?? "",
      });
    } catch {
      nextRenderSignature = String(
        previewRuntime?.renderModel?.VisualId
        ?? previewRuntime?.renderModel?._id
        ?? previewRuntime?.renderModel?.UniqueId
        ?? ""
      );
    }

    if (!nextRenderSignature) {
      return;
    }

    if (previousRenderSignatureRef.current !== nextRenderSignature) {
      previousRenderSignatureRef.current = nextRenderSignature;
      setLastLoadedAt(Date.now());
    }
  }, [previewRuntime?.error, previewRuntime?.isLoading, previewRuntime?.renderModel]);

  useEffect(() => {
    if (!visualId || !visualRefreshCycleId || previewRuntime?.isLoading) {
      return;
    }

    if (lastCompletedRefreshCycleRef.current === visualRefreshCycleId) {
      return;
    }

    lastCompletedRefreshCycleRef.current = visualRefreshCycleId;
    setLastLoadedAt(Date.now());
  }, [previewRuntime?.isLoading, visualId, visualRefreshCycleId]);

  return {
    ...previewRuntime,
    executionMode,
    lastLoadedAt,
  };
};
